// src/hooks/useGroups.ts
import { useState } from "react";
import { apiClient } from "@/services/apiClient";

interface GraphGroup {
  id: string;
  displayName: string;
  description?: string;
  groupTypes?: string[];
  isOwner?: boolean;
}

interface ApiResponse {
  groups: GraphGroup[];
  nextLink?: string | null;
}

export function useGroups() {
  const [groups, setGroups] = useState<GraphGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextLink, setNextLink] = useState<string | null>(null);

  /**
   * Loads the user's Microsoft 365 Groups via the backend API.
   * Backend endpoint: GET /api/group/my-groups?all=false
   */
  const searchGroups = async (query = "") => {
    setLoading(true);
    setError(null);

    try {
      let url = "/api/group/my-groups";
      
      if (query !== "")
        url += `?search=${encodeURIComponent(query)}`;

      console.log("Fetching groups with query:", query);
      console.log("Fetching groups from URL:", url);

      const res = await apiClient.get<ApiResponse>(url);
      const data = res.data;

      const sortedGroups = (data.groups || []).sort((a, b) =>
        (a.displayName || "").localeCompare(b.displayName || "", undefined, { sensitivity: "base" })
      );

      setGroups(sortedGroups);      
      setNextLink(data.nextLink || null);
    } catch (err: any) {
      console.error("Error loading groups:", err);
      setError("Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Loads the next page of groups from a backend pagination link
   */
  const loadNext = async () => {
    if (!nextLink) return;
    try {
      const res = await apiClient.get<ApiResponse>(nextLink);
      const data = res.data;
      
      setGroups((prev) => [...prev, ...(data.groups || [])]);
      setNextLink(data.nextLink || null);
    } catch (err) {
      console.error("Error loading next page:", err);
      setError("Failed to load next page");
    }
  };

  return { groups, loading, error, nextLink, searchGroups, loadNext };
}